import { useEffect, useState, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ApiError, apiGet } from "@/lib/api";

type TimeRange = "7d" | "30d" | "90d";

type DailyCount = { date: string; count: number };
type RankedScene = { sceneId: string; name: string; count: number };
type TopQuestion = { question: string; count: number };
type StatusTotal = { status: string; count: number };
type ShipRoutePair = { shipGroup: string; route: string; direction: string };
type FaceSwapDaily = DailyCount & { failed: number };

type AnalyticsResponse = {
  range: {
    value: TimeRange;
    timezone: string;
    start: string;
    end: string;
    days: number;
  };
  generatedAt: string;
  deviceUsage: DailyCount[];
  shipGroupCount: DailyCount[];
  shipGroupRoutePairs: Record<string, ShipRoutePair[]>;
  faceSwap: { daily: FaceSwapDaily[]; statusTotals: StatusTotal[] };
  qaSceneRanking: RankedScene[];
  topQuestions: TopQuestion[];
};

const ranges: { value: TimeRange; label: string }[] = [
  { value: "7d", label: "7 天" },
  { value: "30d", label: "30 天" },
  { value: "90d", label: "90 天" },
];
const statusLabels: Record<string, { label: string; className: string }> = {
  done: { label: "已完成", className: "bg-emerald-100 text-emerald-700" },
  failed: { label: "失败", className: "bg-rose-100 text-rose-700" },
  processing: { label: "处理中", className: "bg-amber-100 text-amber-700" },
  queued: { label: "排队中", className: "bg-sky-100 text-sky-700" },
};

function ChartEmpty({ message = "该时间范围暂无数据" }: { message?: string }) {
  return <div className="h-full flex items-center justify-center text-sm text-gray-400">{message}</div>;
}

type ChartTooltipPayload = { color?: string; name?: string; value?: number | string };

function TooltipCard({ children }: { children: ReactNode }) {
  return (
    <div className="min-w-[170px] max-w-xs rounded-lg border border-gray-200 bg-white p-3 text-xs shadow-md">
      {children}
    </div>
  );
}

function TooltipHeader({ label }: { label: string | number }) {
  return <p className="border-b border-gray-100 pb-1.5 font-semibold text-gray-900">{label}</p>;
}

function TooltipMetric({ color, label, value, valueClassName = "text-gray-900" }: { color: string; label: string; value: number | string; valueClassName?: string }) {
  return (
    <div className="flex items-center justify-between gap-5">
      <span className="flex items-center gap-1.5 text-gray-600">
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
        {label}
      </span>
      <span className={`font-semibold tabular-nums ${valueClassName}`}>{value} 次</span>
    </div>
  );
}

function ChartTooltip({ active, label, payload }: {
  active?: boolean;
  label?: string | number;
  payload?: ChartTooltipPayload[];
}) {
  if (!active || label === undefined || !payload?.length) return null;
  return (
    <TooltipCard>
      <TooltipHeader label={label} />
      <div className="space-y-1.5 pt-2">
        {payload.map((item, index) => (
          <TooltipMetric
            key={`${item.name}-${index}`}
            color={item.color ?? "#94a3b8"}
            label={item.name ?? "数量"}
            value={item.value ?? 0}
          />
        ))}
      </div>
    </TooltipCard>
  );
}

function ShipRoutePairTooltip({ active, label, payload, pairsByDate }: {
  active?: boolean;
  label?: string | number;
  payload?: ChartTooltipPayload[];
  pairsByDate: Record<string, ShipRoutePair[]>;
}) {
  if (!active || label === undefined) return null;
  const date = String(label);
  const pairs = pairsByDate[date] ?? [];
  return (
    <TooltipCard>
      <TooltipHeader label={date} />
      <div className="pt-2">
        <TooltipMetric color={payload?.[0]?.color ?? "#0284c7"} label="船组-航线组合" value={payload?.[0]?.value ?? 0} />
      </div>
      {pairs.length ? (
        <ul className="mt-2 max-h-[min(24rem,calc(100vh-10rem))] space-y-1 overflow-y-auto overscroll-contain border-t border-gray-100 pt-2 text-gray-600">
          {pairs.map((pair) => (
            <li key={`${pair.shipGroup}-${pair.route}-${pair.direction}`} className="flex items-start justify-between gap-3">
              <span>{pair.shipGroup} · {pair.route}</span>
              <span className="shrink-0 rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">{pair.direction === "up" ? "上行" : pair.direction === "down" ? "下行" : pair.direction || "未标注"}</span>
            </li>
          ))}
        </ul>
      ) : <p className="mt-2 border-t border-gray-100 pt-2 text-gray-400">当天暂无组合</p>}
    </TooltipCard>
  );
}

function FaceSwapTooltip({ active, label, payload }: {
  active?: boolean;
  label?: string | number;
  payload?: Array<ChartTooltipPayload & { payload?: FaceSwapDaily }>;
}) {
  if (!active || label === undefined) return null;
  const daily = payload?.[0]?.payload;
  return (
    <TooltipCard>
      <TooltipHeader label={label} />
      <div className="space-y-1.5 pt-2">
        <TooltipMetric color={payload?.[0]?.color ?? "#f43f5e"} label="任务提交" value={daily?.count ?? 0} />
        <TooltipMetric color="#fb7185" label="失败" value={daily?.failed ?? 0} valueClassName="text-rose-700" />
      </div>
    </TooltipCard>
  );
}

function SectionTitle({ title, description }: { title: string; description: string }) {
  return (
    <div className="mb-4">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <p className="mt-1 text-xs text-gray-500">{description}</p>
    </div>
  );
}

function AnalyticsPage() {
  const [timeRange, setTimeRange] = useState<TimeRange>("7d");
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    apiGet<AnalyticsResponse>(`/analytics?range=${timeRange}`, controller.signal)
      .then((response) => setData(response))
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          setData(null);
          setError(reason instanceof Error ? reason : new Error(String(reason)));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [timeRange, reloadKey]);

  const errorMessage = error instanceof ApiError && error.status === 401
    ? "登录已失效，请重新登录后查看数据。"
    : error instanceof ApiError && error.status === 403
      ? "当前账号没有查看数据分析的权限。"
      : "数据暂时无法加载，请检查服务连接后重试。";

  return (
    <main className="space-y-5" aria-busy={loading}>
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">数据分析</h1>
          <p className="mt-1 text-sm text-gray-500">设备使用、讲解播放、AI 问答和换脸任务的运营数据</p>
        </div>
        <div className="flex items-center rounded-lg bg-gray-100 p-1" aria-label="选择统计时间范围">
          {ranges.map((range) => (
            <button
              key={range.value}
              type="button"
              aria-pressed={timeRange === range.value}
              disabled={loading}
              onClick={() => setTimeRange(range.value)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-wait ${
                timeRange === range.value ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </header>

      {error ? (
        <section className="rounded-lg border border-rose-200 bg-rose-50 p-4" role="alert">
          <p className="text-sm font-medium text-rose-800">{errorMessage}</p>
          <button type="button" onClick={() => setReloadKey((key) => key + 1)} className="mt-2 text-sm font-medium text-rose-700 underline hover:text-rose-900">
            重试
          </button>
        </section>
      ) : null}

      {data ? (
        <p className="text-xs text-gray-400" aria-live="polite">
          统计范围：{data.range.start} 至 {data.range.end}（{data.range.timezone}）；更新于 {new Date(data.generatedAt).toLocaleString("zh-CN", { hour12: false })}
        </p>
      ) : null}


      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <SectionTitle title="每日设备使用次数" description="按有效激活订单统计，单位：次" />
          <div className="h-64">
            {loading ? <ChartEmpty message="正在加载设备使用数据…" /> : data?.deviceUsage.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.deviceUsage}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
                  <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Line type="monotone" dataKey="count" name="设备使用" stroke="#0d9488" strokeWidth={2} dot={{ fill: "#0d9488", r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <ChartEmpty />}
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <SectionTitle title="每日船组-航线组合数量" description="按当日有效订单的去重船组、航线和方向组合统计，单位：个" />
          <div className="h-64">
            {loading ? <ChartEmpty message="正在加载船组-航线组合数据…" /> : data?.shipGroupCount.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.shipGroupCount}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
                  <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
                  <Tooltip content={<ShipRoutePairTooltip pairsByDate={data.shipGroupRoutePairs} />} />
                  <Bar dataKey="count" name="船组-航线组合" fill="#0284c7" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <ChartEmpty />}
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <SectionTitle title="换脸任务提交量" description="按任务提交日期统计，单位：次" />
          <div className="h-64">
            {loading ? <ChartEmpty message="正在加载换脸任务数据…" /> : data?.faceSwap.daily.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.faceSwap.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
                  <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
                  <Tooltip content={<FaceSwapTooltip />} />
                  <Bar dataKey="count" name="任务提交" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <ChartEmpty />}
          </div>
          <div className="mt-3 flex flex-wrap gap-2" aria-label="换脸任务当前状态汇总">
            {data?.faceSwap.statusTotals.map(({ status, count }) => {
              const definition = statusLabels[status] ?? { label: status, className: "bg-gray-100 text-gray-600" };
              return <span key={status} className={`rounded-full px-2.5 py-1 text-xs font-medium ${definition.className}`}>{definition.label} {count}</span>;
            })}
            {!loading && !data?.faceSwap.statusTotals.length ? <span className="text-xs text-gray-400">暂无任务状态数据</span> : null}
          </div>
        </section>
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <SectionTitle title="景点讲解播放量 Top 10" description="按设备端上报的实际讲解播放事件统计，单位：次" />
          <div className="h-72">
            {loading ? <ChartEmpty message="正在加载讲解播放数据…" /> : data?.narrationSceneRanking.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.narrationSceneRanking} layout="vertical" margin={{ left: 12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={100} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="count" name="播放量" fill="#0d9488" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <ChartEmpty />}
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <SectionTitle title="景点 AI 问答量 Top 10" description="按已记录的 AI 问答统计，单位：次" />
          <div className="h-72">
            {loading ? <ChartEmpty message="正在加载 AI 问答数据…" /> : data?.qaSceneRanking.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.qaSceneRanking} layout="vertical" margin={{ left: 12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={100} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="count" name="问答量" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <ChartEmpty />}
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <SectionTitle title="AI 高频问题" description="按原始问题文本聚合，单位：次" />
        {loading ? <p className="text-sm text-gray-400">正在加载高频问题…</p> : data?.topQuestions.length ? (
          <ol className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {data.topQuestions.map(({ question, count }) => (
              <li key={question} className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2 text-sm">
                <span className="truncate text-gray-700" title={question}>{question}</span>
                <span className="shrink-0 font-medium text-teal-700">{count} 次</span>
              </li>
            ))}
          </ol>
        ) : <p className="text-sm text-gray-400">该时间范围暂无 AI 问答记录。</p>}
      </section>
      </div>
    </main>
  );
}

export default AnalyticsPage;
